import React, { useMemo } from "react"
import PropTypes from "prop-types"
import {
  ResponsiveContainer,
  PieChart, Pie, Cell, Tooltip, Legend,
  BarChart, Bar, XAxis, YAxis, CartesianGrid,
} from "recharts"
import {
  formatCurrency, formatNumber, formatPercent, formatGrowth, formatFecha,
  familiaConfig, coberturaConfig, colorPara,
} from "../helpers"

const StatCard = ({ icon, label, value, accent = "#3b82f6", sub }) => (
  <div style={{
    flex: "1 1 200px",
    background: "#fff", border: "1px solid #e2e8f0", borderRadius: 14,
    padding: "16px 20px", minWidth: 0,
  }}>
    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
      <div style={{
        width: 36, height: 36, borderRadius: 10,
        background: `${accent}1a`, color: accent,
        display: "flex", alignItems: "center", justifyContent: "center",
        fontSize: 18, flexShrink: 0,
      }}>
        <i className={`mdi ${icon}`} />
      </div>
      <span style={{ fontSize: 11, fontWeight: 600, color: "#64748b", textTransform: "uppercase", letterSpacing: 0.4 }}>
        {label}
      </span>
    </div>
    <div style={{ fontSize: 22, fontWeight: 700, color: "#0f172a", lineHeight: 1.1 }}>{value}</div>
    {sub && <div style={{ fontSize: 12, color: "#94a3b8", marginTop: 4 }}>{sub}</div>}
  </div>
)

StatCard.propTypes = { icon: PropTypes.string, label: PropTypes.string, value: PropTypes.node, accent: PropTypes.string, sub: PropTypes.node }

// Card de composición Gravado vs Excluido (porcentajes que SÍ se pueden agregar)
const ComposicionCard = ({ pctGravado, pctExcluido, totalLineas, totalSucursales }) => {
  const g = Number(pctGravado) || 0
  const e = Number(pctExcluido) || 0
  return (
    <div style={{ width: "100%", display: "flex", flexDirection: "column", gap: 12 }}>
      <div style={{ display: "flex", gap: 8 }}>
        <div style={{ flex: g, background: "#3b82f6", borderRadius: 6, padding: "12px 14px", color: "#fff" }}>
          <div style={{ fontSize: 10, textTransform: "uppercase", fontWeight: 700, opacity: 0.9 }}>Gravado</div>
          <div style={{ fontSize: 22, fontWeight: 700, lineHeight: 1, marginTop: 4 }}>{formatPercent(g, 2)}</div>
        </div>
        {e > 0 && (
          <div style={{ flex: Math.max(e, 6), background: "#f59e0b", borderRadius: 6, padding: "12px 14px", color: "#fff", minWidth: 80 }}>
            <div style={{ fontSize: 10, textTransform: "uppercase", fontWeight: 700, opacity: 0.9 }}>Excluido</div>
            <div style={{ fontSize: 22, fontWeight: 700, lineHeight: 1, marginTop: 4 }}>{formatPercent(e, 2)}</div>
          </div>
        )}
      </div>
      <div style={{ fontSize: 12, color: "#475569", textAlign: "center" }}>
        <span><strong>{formatNumber(totalSucursales)}</strong> sucursales</span>
        {" · "}
        <span><strong>{formatNumber(totalLineas)}</strong> líneas</span>
      </div>
    </div>
  )
}

ComposicionCard.propTypes = {
  pctGravado: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  pctExcluido: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  totalLineas: PropTypes.number,
  totalSucursales: PropTypes.number,
}

const ResumenTab = ({ data }) => {
  const totales = data?.totales_general || {}
  const familias = useMemo(() => data?.por_familia || [], [data])
  const top = useMemo(() => data?.top_impuestos || [], [data])
  const insights = data?.insights || []
  const comp = data?.comparativo
  const completitud = data?.completitud || {}
  const totalSucursales = data?.total_sucursales ?? data?.sucursales?.length ?? 0

  // Excluidos = productos sin tipo de impuesto (campo nuevo). Fallback a `total_exentos`
  // para compat con back legacy donde el dato vivía mal etiquetado en `exentos`.
  const totalExcluidos = totales.total_excluidos ?? totales.total_exentos ?? 0
  const pctExcluido = totales.porcentaje_excluido ?? totales.porcentaje_exento ?? 0

  // Donut data por familia
  const donutFamilias = useMemo(() =>
    familias.map((f, i) => ({
      name: f.familia,
      value: Number(f.valor_impuesto) || 0,
      participacion: Number(f.participacion_pct) || 0,
      base: Number(f.base_gravable) || 0,
      tipos: Number(f.cantidad_tipos) || 0,
      color: familiaConfig(f.familia).color || colorPara(i),
    })),
    [familias],
  )

  // Bar data familia: base vs impuesto
  const barFamilias = useMemo(() =>
    familias.map(f => ({
      familia: f.familia,
      base_gravable: Number(f.base_gravable) || 0,
      valor_impuesto: Number(f.valor_impuesto) || 0,
    })),
    [familias],
  )

  if (!data) {
    return (
      <div style={{ padding: 60, textAlign: "center", color: "#64748b" }}>
        <i className="mdi mdi-cash-register" style={{ fontSize: 48, color: "#cbd5e1" }} />
        <div className="mt-2">Sin datos.</div>
      </div>
    )
  }

  const cobertura = coberturaConfig(completitud.cobertura)
  const growthImp = comp ? formatGrowth(comp.crecimiento_pct) : null
  const growthBase = comp ? formatGrowth(comp.crecimiento_base_pct) : null

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {/* KPIs principales */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
        <StatCard icon="mdi-cash-multiple" label="Base gravable"
          value={formatCurrency(totales.total_base_gravable)} accent="#3b82f6"
          sub={`${formatPercent(totales.porcentaje_gravado, 2)} del total`} />
        <StatCard icon="mdi-bank" label="Total impuestos"
          value={formatCurrency(totales.total_impuestos)} accent="#10b981"
          sub={`${formatNumber(totales.total_lineas)} líneas`} />
        <StatCard icon="mdi-cash" label="Total con impuestos"
          value={formatCurrency(totales.total_con_impuestos)} accent="#8b5cf6" />
        <StatCard icon="mdi-shield-off-outline" label="Excluidos"
          value={formatCurrency(totalExcluidos)} accent="#f59e0b"
          sub={`${formatPercent(pctExcluido, 2)} del total · sin tipo de impuesto`} />
      </div>

      {/* Composición gravado/excluido + Top impuestos lado a lado */}
      <div style={{ display: "grid", gridTemplateColumns: "minmax(280px, 1fr) 2fr", gap: 12 }}>
        <div style={{
          background: "#fff", border: "1px solid #e2e8f0", borderRadius: 14,
          padding: 16, display: "flex", flexDirection: "column",
        }}>
          <h6 style={{ margin: 0, color: "#0f172a", fontWeight: 600, marginBottom: 12 }}>
            <i className="mdi mdi-chart-pie-outline me-2 text-primary" />
            Composición gravado / excluido
          </h6>
          <ComposicionCard
            pctGravado={totales.porcentaje_gravado}
            pctExcluido={pctExcluido}
            totalLineas={totales.total_lineas}
            totalSucursales={totalSucursales}
          />
        </div>

        <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 14, padding: 16 }}>
          <div className="d-flex justify-content-between align-items-baseline mb-2">
            <h6 style={{ margin: 0, color: "#0f172a", fontWeight: 600 }}>
              <i className="mdi mdi-trophy-outline me-2 text-warning" />
              Top impuestos por valor
            </h6>
            <span style={{ fontSize: 11, color: "#94a3b8" }}>{top.length} tipos</span>
          </div>
          {top.length === 0 ? (
            <div style={{ padding: 30, textAlign: "center", color: "#94a3b8" }}>Sin datos.</div>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                <thead>
                  <tr>
                    <th style={{ padding: "8px 10px", textAlign: "left", fontSize: 10, color: "#64748b", fontWeight: 700, textTransform: "uppercase", borderBottom: "1px solid #e2e8f0" }}>Tipo</th>
                    <th style={{ padding: "8px 10px", textAlign: "left", fontSize: 10, color: "#64748b", fontWeight: 700, textTransform: "uppercase", borderBottom: "1px solid #e2e8f0" }}>Nombre</th>
                    <th style={{ padding: "8px 10px", textAlign: "center", fontSize: 10, color: "#64748b", fontWeight: 700, textTransform: "uppercase", borderBottom: "1px solid #e2e8f0" }}>Tasa</th>
                    <th style={{ padding: "8px 10px", textAlign: "right", fontSize: 10, color: "#64748b", fontWeight: 700, textTransform: "uppercase", borderBottom: "1px solid #e2e8f0" }}>Valor</th>
                    <th style={{ padding: "8px 10px", textAlign: "right", fontSize: 10, color: "#64748b", fontWeight: 700, textTransform: "uppercase", borderBottom: "1px solid #e2e8f0" }}>Participación</th>
                  </tr>
                </thead>
                <tbody>
                  {top.slice(0, 8).map((t, i) => (
                    <tr key={i} style={{ background: i % 2 === 0 ? "#fff" : "#fafbfc" }}>
                      <td style={{ padding: "8px 10px", borderBottom: "1px solid #f1f5f9", fontFamily: "monospace", fontWeight: 600, color: "#1e40af" }}>
                        {t.tipo_impuesto}
                      </td>
                      <td style={{ padding: "8px 10px", borderBottom: "1px solid #f1f5f9" }}>
                        {t.nombre_impuesto}
                      </td>
                      <td style={{ padding: "8px 10px", textAlign: "center", borderBottom: "1px solid #f1f5f9" }}>
                        <span style={{
                          padding: "2px 8px", borderRadius: 999, fontSize: 11, fontWeight: 700,
                          background: "#eff6ff", color: "#1e40af",
                        }}>{formatPercent(t.tasa, 0)}</span>
                      </td>
                      <td style={{ padding: "8px 10px", textAlign: "right", fontWeight: 600, color: "#10b981", borderBottom: "1px solid #f1f5f9" }}>
                        {formatCurrency(t.valor_impuesto)}
                      </td>
                      <td style={{ padding: "8px 10px", textAlign: "right", borderBottom: "1px solid #f1f5f9" }}>
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: 8 }}>
                          <div style={{
                            width: 60, height: 6, background: "#e2e8f0", borderRadius: 999, overflow: "hidden",
                          }}>
                            <div style={{
                              width: `${Math.min(100, t.participacion_pct)}%`,
                              height: "100%", background: "#3b82f6",
                            }} />
                          </div>
                          <strong>{formatPercent(t.participacion_pct, 1)}</strong>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Donut por familia + Bars base vs impuesto */}
      {familias.length > 0 && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(360px, 1fr))", gap: 12 }}>
          <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 14, padding: 16 }}>
            <h6 style={{ margin: 0, color: "#0f172a", fontWeight: 600, marginBottom: 8 }}>
              <i className="mdi mdi-chart-donut me-2 text-primary" />
              Distribución por familia
            </h6>
            <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
              <div style={{ flex: "1 1 220px", minWidth: 220, height: 240 }}>
                <ResponsiveContainer>
                  <PieChart>
                    <Pie
                      data={donutFamilias}
                      dataKey="value" nameKey="name"
                      innerRadius={60} outerRadius={90} paddingAngle={2}
                    >
                      {donutFamilias.map((d, i) => <Cell key={i} fill={d.color} />)}
                    </Pie>
                    <Tooltip
                      formatter={(v, _, p) => [
                        formatCurrency(v),
                        `${p.payload.name} (${formatPercent(p.payload.participacion, 2)})`,
                      ]}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
              <div style={{ flex: "1 1 160px", display: "flex", flexDirection: "column", gap: 6 }}>
                {donutFamilias.map((d, i) => (
                  <div key={i} style={{
                    display: "flex", alignItems: "center", justifyContent: "space-between",
                    fontSize: 12, gap: 8,
                  }}>
                    <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <span style={{ width: 10, height: 10, borderRadius: 3, background: d.color }} />
                      <strong>{d.name}</strong>
                      {d.tipos > 1 && <span style={{ color: "#94a3b8", fontSize: 10 }}>· {d.tipos} tipos</span>}
                    </span>
                    <span style={{ color: "#475569", fontWeight: 600 }}>
                      {formatPercent(d.participacion, 1)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 14, padding: 16 }}>
            <h6 style={{ margin: 0, color: "#0f172a", fontWeight: 600, marginBottom: 8 }}>
              <i className="mdi mdi-chart-bar me-2 text-primary" />
              Base gravable vs Impuesto por familia
            </h6>
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={barFamilias} margin={{ left: 0, right: 12 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="familia" tick={{ fontSize: 12 }} />
                <YAxis tick={{ fontSize: 11 }} tickFormatter={v => formatCurrency(v).replace("COP", "").trim()} />
                <Tooltip formatter={v => formatCurrency(v)} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Bar dataKey="base_gravable" name="Base gravable" fill="#94a3b8" radius={[6, 6, 0, 0]} />
                <Bar dataKey="valor_impuesto" name="Valor impuesto" fill="#3b82f6" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Comparativo banner */}
      {comp && (
        <div style={{
          background: "linear-gradient(135deg, #eff6ff, #ffffff)",
          border: "1px solid #bfdbfe", borderLeft: "4px solid #3b82f6",
          borderRadius: 14, padding: "14px 18px",
        }}>
          <div className="d-flex justify-content-between align-items-baseline flex-wrap gap-2 mb-2">
            <h6 style={{ margin: 0, color: "#1e40af", fontWeight: 700 }}>
              <i className="mdi mdi-compare-horizontal me-2" />
              Comparativo vs período anterior
            </h6>
            <span style={{ fontSize: 11, color: "#475569" }}>
              {formatFecha(comp.periodo_anterior_inicio)} — {formatFecha(comp.periodo_anterior_fin)}
            </span>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 10 }}>
            <div>
              <div style={{ fontSize: 11, color: "#64748b", textTransform: "uppercase", fontWeight: 700 }}>Impuestos anterior</div>
              <div style={{ fontSize: 16, fontWeight: 700, color: "#0f172a" }}>{formatCurrency(comp.impuestos_anterior)}</div>
            </div>
            <div>
              <div style={{ fontSize: 11, color: "#64748b", textTransform: "uppercase", fontWeight: 700 }}>Impuestos actual</div>
              <div style={{ fontSize: 16, fontWeight: 700, color: "#10b981" }}>{formatCurrency(comp.impuestos_actual)}</div>
            </div>
            <div>
              <div style={{ fontSize: 11, color: "#64748b", textTransform: "uppercase", fontWeight: 700 }}>Crecimiento impuestos</div>
              <div style={{ fontSize: 18, fontWeight: 800, color: growthImp.color }}>
                <i className={`mdi mdi-${growthImp.icon} me-1`} />
                {growthImp.text}
              </div>
            </div>
            <div>
              <div style={{ fontSize: 11, color: "#64748b", textTransform: "uppercase", fontWeight: 700 }}>Crecimiento base</div>
              <div style={{ fontSize: 18, fontWeight: 800, color: growthBase.color }}>
                <i className={`mdi mdi-${growthBase.icon} me-1`} />
                {growthBase.text}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Cobertura + Insights */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: 12 }}>
        {/* Cobertura */}
        <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 14, padding: 16 }}>
          <h6 style={{ margin: 0, color: "#0f172a", fontWeight: 600, marginBottom: 10 }}>
            <i className="mdi mdi-database-check-outline me-2 text-primary" />
            Cobertura de datos
          </h6>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <span style={{
              padding: "5px 14px", borderRadius: 999,
              background: cobertura.bg, color: cobertura.color,
              fontSize: 13, fontWeight: 700,
              display: "inline-flex", alignItems: "center", gap: 6,
            }}>
              <i className={`mdi mdi-${cobertura.icon}`} />
              {cobertura.label}
            </span>
            {completitud.uso_tabla_unificada && (
              <span style={{
                padding: "3px 10px", borderRadius: 999,
                background: "#eff6ff", color: "#1e40af",
                fontSize: 11, fontWeight: 700,
              }}>
                Tabla unificada ✓
              </span>
            )}
          </div>
          <div style={{ fontSize: 12, color: "#475569", marginTop: 10, lineHeight: 1.6 }}>
            {completitud.fecha_corte_data && (
              <div>
                <i className="mdi mdi-clock-outline me-1" />
                Corte de datos: <strong>{formatFecha(completitud.fecha_corte_data)}</strong>
              </div>
            )}
            {completitud.lineas_multi_impuesto != null && (
              <div>
                <i className="mdi mdi-layers-outline me-1" />
                Líneas con múltiples impuestos: <strong>{formatNumber(completitud.lineas_multi_impuesto)}</strong>
              </div>
            )}
          </div>
        </div>

        {/* Insights */}
        {insights.length > 0 && (
          <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 14, padding: 16 }}>
            <h6 style={{ margin: 0, color: "#0f172a", fontWeight: 600, marginBottom: 10 }}>
              <i className="mdi mdi-lightbulb-on-outline me-2 text-warning" />
              Insights
            </h6>
            <ul style={{ margin: 0, paddingLeft: 20, color: "#475569", fontSize: 13, lineHeight: 1.8 }}>
              {insights.map((ins, i) => <li key={i}>{ins}</li>)}
            </ul>
          </div>
        )}
      </div>
    </div>
  )
}

ResumenTab.propTypes = { data: PropTypes.object }

export default ResumenTab
